import {
  Button,
  ButtonGroup,
  COLOR,
  getColorClassName,
  useMatchMediaTierProp,
} from "@george-labs.com/design-system"
import { useTranslation } from "react-i18next"
import { useSaveModal } from "./OfferModalContext"
import { ICON } from "@george-labs.com/george-assets/icons"

interface IOfferNavButtonsProps {
  isAlternativeColored?: boolean
  nextButtonText?: string
  prevButtonText?: string
  onPrevButtonClicked?: () => void
  onNextButtonClicked?: () => void
}

const OfferNavButtons: React.FC<IOfferNavButtonsProps> = ({
  isAlternativeColored,
  nextButtonText,
  prevButtonText,
  onPrevButtonClicked,
  onNextButtonClicked,
}) => {
  const { t } = useTranslation()
  const nextButtonTitle = nextButtonText || t("offers.button.next")

  const nextButtonClasses = isAlternativeColored
    ? `
      ${getColorClassName(COLOR.WHITE)}
      ${getColorClassName(COLOR.TEXT_PRIMARY, { foreground: true })}`
    : ""

  const nextButtonContent = (
    <>
      {!onNextButtonClicked && (
        <Button
          type="submit"
          variant={Button.VARIANT.PRIMARY}
          className={nextButtonClasses}
        >
          {nextButtonTitle}
        </Button>
      )}

      {!!onNextButtonClicked && (
        <Button
          type="button"
          variant={Button.VARIANT.PRIMARY}
          onClick={onNextButtonClicked}
          className={nextButtonClasses}
        >
          {nextButtonTitle}
        </Button>
      )}
    </>
  )

  const prevButtonContent = useMatchMediaTierProp({
    xs: (
      <Button
        type="button"
        className={getColorClassName(COLOR.WHITE)}
        onClick={onPrevButtonClicked}
      >
        {prevButtonText || t("offers.button.back")}
      </Button>
    ),
    md: (
      <Button
        type="button"
        className={getColorClassName(COLOR.WHITE)}
        variant={Button.VARIANT.SECONDARY}
        onClick={onPrevButtonClicked}
      >
        {prevButtonText || t("offers.button.back")}
      </Button>
    ),
  })

  const buttonsContent = useMatchMediaTierProp({
    xs: (
      <ButtonGroup className="text-center">
        {nextButtonContent}
        {!!onPrevButtonClicked && prevButtonContent}
      </ButtonGroup>
    ),
    md: (
      <ButtonGroup className="text-center">
        {!!onPrevButtonClicked && prevButtonContent}
        {nextButtonContent}
      </ButtonGroup>
    ),
  })

  return buttonsContent
}

interface IOfferSaveButtonProps {
  isAlternativeColored?: boolean
  saveButtonText?: string
  skipModal?: boolean
  onSaveButtonClicked?: () => void
}

const OfferSaveButton: React.FC<IOfferSaveButtonProps> = ({
  isAlternativeColored,
  saveButtonText,
  skipModal,
  onSaveButtonClicked,
}) => {
  const { t } = useTranslation()
  const { openSaveModal } = useSaveModal()

  const saveButtonClasses = isAlternativeColored
    ? `${getColorClassName(COLOR.WHITE, { foreground: true })}`
    : ""

  const handleClickWithModal = () => {
    openSaveModal(onSaveButtonClicked)
  }

  const handleClickDirect = () => {
    onSaveButtonClicked()
  }

  return (
    <ButtonGroup className="text-center">
      <Button
        type="button"
        variant={Button.VARIANT.TERTIARY}
        onClick={skipModal ? handleClickDirect : handleClickWithModal}
        className={saveButtonClasses}
      >
        {saveButtonText || t("offers.button.save")}
      </Button>
    </ButtonGroup>
  )
}

interface IOfferActionButtonProps {
  actionButtonText: string
  className?: string
  icon?: ICON
  hideIconXs?: boolean
  onActionButtonClicked: () => void
}

const OfferActionButton: React.FC<IOfferActionButtonProps> = ({
  actionButtonText,
  className,
  hideIconXs,
  icon,
  onActionButtonClicked,
}) => {
  const buttonContent = useMatchMediaTierProp({
    xs: (
      <Button
        icon={hideIconXs ? null : icon}
        variant={Button.VARIANT.TERTIARY}
        onClick={onActionButtonClicked}
        className={`${className || ""} ${getColorClassName(COLOR.UI_BLUE_WEAK)} ${getColorClassName(COLOR.UI_BLUE, { foreground: true })}`}
      >
        {actionButtonText}
      </Button>
    ),
    md: (
      <Button
        icon={icon || null}
        variant={Button.VARIANT.SECONDARY}
        onClick={onActionButtonClicked}
        className={className || ""}
      >
        {actionButtonText}
      </Button>
    ),
  })

  return <>{buttonContent}</>
}

export { OfferActionButton, OfferNavButtons, OfferSaveButton }
